import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './APropos.css';
import Header from '../Header/Header';
import Footer from '../footer/footer';
import TopAnnouncementBar from '../TopAnnouncementBar/TopAnnouncementBar';
import WhatsAppButton from '../whatsapp/WhatsAppButton';
import { Link } from 'react-router-dom';
import { 
  FaRocket, FaSearch, FaChartLine, FaCode, FaMobileAlt, FaRobot, 
  FaCheckCircle, FaArrowRight, FaUsers, FaShieldAlt, FaClock,
  FaRegLightbulb, FaRegHandshake, FaRocket as FaRocketIcon,
  FaCalendarAlt, FaFileAlt, FaLaptopCode, FaMagic, FaStar
} from 'react-icons/fa';
import { MdAutoAwesome, MdOutlineSpeed, MdOutlineSecurity, MdOutlineSupportAgent, MdAnalytics } from 'react-icons/md';
import { GiAstronautHelmet, GiStarsStack, GiGalaxy, GiSpiralShell, GiCrystalBall, GiCyberEye, GiArtificialIntelligence, GiBrain, GiRocketThruster } from 'react-icons/gi';

const CommentCaMarche = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isCursorActive, setIsCursorActive] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const sectionRef = useRef(null);
  const stepsRef = useRef([]);
  let cursorTimeout;

  const steps = [
    {
      number: "01",
      title: "Audit & Découverte",
      subtitle: "On analyse votre projet",
      description: "Nous commençons par une analyse approfondie de vos besoins, objectifs et contraintes. Un audit complet de votre situation actuelle nous permet de définir une stratégie sur-mesure.",
      details: [
        "Analyse des besoins et objectifs",
        "Audit technique et stratégique",
        "Identification des opportunités",
        "Définition des KPI de succès"
      ],
      icon: <FaSearch />,
      color: "#3B82F6",
      duration: "1-2 semaines"
    },
    {
      number: "02",
      title: "Stratégie & Planning",
      subtitle: "On construit votre roadmap",
      description: "Nous élaborons une stratégie digitale complète avec un planning détaillé. Chaque étape est définie avec des livrables clairs et des deadlines précises.",
      details: [
        "Définition de la stratégie globale",
        "Planning et jalons clés",
        "Choix des technologies",
        "Validation des livrables"
      ],
      icon: <FaChartLine />,
      color: "#8B5CF6",
      duration: "1 semaine"
    },
    {
      number: "03",
      title: "Design & Prototypage",
      subtitle: "On donne vie à vos idées",
      description: "Nos designers créent des maquettes interactives et des prototypes qui reflètent votre identité et vos ambitions. Vous validez chaque étape avant le développement.",
      details: [
        "Wireframes et maquettes",
        "Prototypes interactifs",
        "Design UI/UX personnalisé",
        "Validations itératives"
      ],
      icon: <FaLaptopCode />,
      color: "#10B981",
      duration: "2-3 semaines"
    },
    {
      number: "04",
      title: "Développement",
      subtitle: "On construit votre solution",
      description: "Nos développeurs transforment les designs en code fonctionnel, avec des tests continus pour garantir qualité et performance.",
      details: [
        "Développement Agile",
        "Tests unitaires et intégration",
        "Revues de code régulières",
        "Documentation technique"
      ],
      icon: <FaCode />,
      color: "#F59E0B",
      duration: "4-8 semaines"
    },
    {
      number: "05",
      title: "Tests & Optimisation",
      subtitle: "On peaufine chaque détail",
      description: "Une phase de tests rigoureuse garantit que votre solution est performante, sécurisée et sans bug. Nous optimisons chaque aspect avant le lancement.",
      details: [
        "Tests fonctionnels",
        "Tests de performance",
        "Tests de sécurité",
        "Optimisations finales"
      ],
      icon: <FaMagic />,
      color: "#EC4899",
      duration: "1-2 semaines"
    },
    {
      number: "06",
      title: "Lancement & Support",
      subtitle: "On vous accompagne",
      description: "Nous déployons votre solution et restons à vos côtés avec un support dédié. Une formation est fournie pour que vous soyez autonome.",
      details: [
        "Déploiement en production",
        "Support prioritaire 24/7",
        "Formation des équipes",
        "Suivi post-lancement"
      ],
      icon: <FaRocketIcon />,
      color: "#06B6D4",
      duration: "Continu"
    }
  ];

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
      setIsCursorActive(true);
      clearTimeout(cursorTimeout);
      cursorTimeout = setTimeout(() => setIsCursorActive(false), 100);
    };
    
    const handleScroll = () => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        const scrollPercent = Math.min(1, Math.max(0, -rect.top / (rect.height - window.innerHeight)));
        setScrollProgress(scrollPercent);
      }
      
      // Update active step based on scroll position
      stepsRef.current.forEach((step, index) => {
        if (step) {
          const rect = step.getBoundingClientRect();
          if (rect.top < window.innerHeight * 0.6 && rect.bottom > window.innerHeight * 0.3) {
            setActiveStep(index);
          }
        }
      });
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(cursorTimeout);
    };
  }, []);

  return (
    <div className="cmc-container" ref={sectionRef}>
      <WhatsAppButton />
      <TopAnnouncementBar />
      <Header />
      
      {/* Cursor Glow */}
      <motion.div 
        className={`cmc-cursor-glow ${isCursorActive ? 'active' : ''}`}
        animate={{
          x: mousePosition.x - 300,
          y: mousePosition.y - 300,
        }}
        transition={{ type: "spring", damping: 30, stiffness: 200, mass: 0.5 }}
      />

      {/* Particles */}
      <div className="cmc-particles">
        {[...Array(60)].map((_, i) => (
          <div key={i} className="cmc-particle" style={{
            left: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 20}s`,
            animationDuration: `${20 + Math.random() * 30}s`,
            width: `${1.5 + Math.random() * 3}px`,
            height: `${1.5 + Math.random() * 3}px`,
          }} />
        ))}
      </div>

      {/* Animated Orbs */}
      <div className="cmc-orbs">
        <div className="cmc-orb cmc-orb-1" style={{ transform: `translate(${scrollProgress * 100}px, ${scrollProgress * 50}px)` }} />
        <div className="cmc-orb cmc-orb-2" style={{ transform: `translate(-${scrollProgress * 80}px, -${scrollProgress * 60}px)` }} />
        <div className="cmc-orb cmc-orb-3" style={{ transform: `translate(${scrollProgress * 60}px, -${scrollProgress * 40}px)` }} />
        <div className="cmc-orb cmc-orb-4" style={{ transform: `translate(-${scrollProgress * 70}px, ${scrollProgress * 70}px)` }} />
      </div>

      {/* Floating Elements */}
      <div className="cmc-floating">
        <GiGalaxy className="cmc-float cmc-float-1" />
        <GiSpiralShell className="cmc-float cmc-float-2" />
        <GiAstronautHelmet className="cmc-float cmc-float-3" />
        <GiStarsStack className="cmc-float cmc-float-4" />
        <GiCrystalBall className="cmc-float cmc-float-5" />
        <GiCyberEye className="cmc-float cmc-float-6" />
        <GiArtificialIntelligence className="cmc-float cmc-float-7" />
        <GiBrain className="cmc-float cmc-float-8" />
        <GiRocketThruster className="cmc-float cmc-float-9" />
      </div>
      
      {/* ========== HERO BANNER ========== */}
      <section className="cmc-hero">
        <div className="cmc-hero-overlay"></div>
        <div className="cmc-hero-inner">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="cmc-hero-badge"
          >
            <GiRocketThruster />
            <span>Notre méthodologie</span>
            <GiStarsStack />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="cmc-hero-title"
          >
            Comment <span className="cmc-gradient-text">ça marche ?</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="cmc-hero-subtitle"
          >
            Une méthodologie claire et transparente pour transformer votre projet en succès.
            De l'audit au lancement, nous vous accompagnons à chaque étape.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="cmc-hero-buttons"
          >
            <Link to="/reservation-appel">
              <button className="cmc-btn cmc-btn-primary">
                <MdAutoAwesome /> Démarrer mon projet <FaArrowRight />
              </button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ========== VERTICAL TIMELINE SECTION ========== */}
      <section className="cmc-timeline">
        <div className="cmc-container-inner">
          {/* Vertical breadcrumb - Side navigation */}
          <div className="cmc-breadcrumb">
            <div className="breadcrumb-line"></div>
            {steps.map((step, idx) => (
              <motion.div
                key={idx}
                className={`breadcrumb-item ${activeStep === idx ? 'active' : ''}`}
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.1 }}
                onClick={() => {
                  stepsRef.current[idx]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }}
              >
                <div className="breadcrumb-dot" style={{ backgroundColor: step.color }}>
                  <span>{idx + 1}</span>
                </div>
                <div className="breadcrumb-label">
                  <span className="breadcrumb-number">{step.number}</span>
                  <span className="breadcrumb-title">{step.title}</span>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Timeline steps content */}
          <div className="cmc-steps">
            {steps.map((step, idx) => (
              <motion.div
                key={idx}
                ref={el => stepsRef.current[idx] = el}
                className={`cmc-step ${activeStep === idx ? 'active' : ''}`}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: idx * 0.1 }}
              >
                <div className="step-card" style={{ '--step-color': step.color }}>
                  <div className="step-header">
                    <div className="step-number" style={{ background: step.color }}>
                      {step.number}
                    </div>
                    <div className="step-icon" style={{ color: step.color }}>
                      {step.icon}
                    </div>
                    <div className="step-duration">
                      <FaClock />
                      <span>{step.duration}</span>
                    </div>
                  </div>
                  
                  <div className="step-content">
                    <h3 className="step-title">{step.title}</h3>
                    <p className="step-subtitle">{step.subtitle}</p>
                    <p className="step-description">{step.description}</p>
                    
                    <div className="step-details">
                      <h4>Ce qui est inclus :</h4>
                      <ul>
                        {step.details.map((detail, i) => (
                          <li key={i}>
                            <FaCheckCircle style={{ color: step.color }} />
                            <span>{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  
                  <div className="step-decoration">
                    <div className="step-glow" style={{ background: `radial-gradient(circle, ${step.color}30, transparent)` }} />
                  </div>
                </div>
                
                {/* Connector line between steps */}
                {idx < steps.length - 1 && (
                  <div className="step-connector">
                    <div className="connector-line"></div>
                    <div className="connector-dot" style={{ backgroundColor: step.color }}></div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== BENEFITS SECTION ========== */}
      <section className="cmc-benefits">
        <div className="cmc-container-inner">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="cmc-section-header"
          >
            <div className="cmc-badge">Pourquoi nous</div>
            <h2>Une méthodologie <span className="cmc-gradient-text">qui fait la différence</span></h2>
            <p>Transparence, qualité et accompagnement à chaque étape</p>
          </motion.div>

          <div className="cmc-benefits-grid">
            {[
              { icon: <FaRegLightbulb />, title: "Méthode Agile", description: "Adaptabilité et réactivité tout au long du projet", color: "#3B82F6" },
              { icon: <FaRegHandshake />, title: "Transparence totale", description: "Reporting régulier et communication claire", color: "#10B981" },
              { icon: <FaStar />, title: "Qualité garantie", description: "Tests rigoureux et livrables validés", color: "#F59E0B" },
              { icon: <FaUsers />, title: "Accompagnement", description: "Support dédié et formation inclus", color: "#EC4899" },
              { icon: <FaRocketIcon />, title: "Livraison rapide", description: "Deadlines respectées et déploiement optimisé", color: "#8B5CF6" },
              { icon: <FaShieldAlt />, title: "Sécurité renforcée", description: "Protection des données et conformité RGPD", color: "#06B6D4" }
            ].map((benefit, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                whileHover={{ y: -8 }}
                className="cmc-benefit-card"
                style={{ '--cmc-card-color': benefit.color }}
              >
                <div className="cmc-benefit-icon" style={{ color: benefit.color }}>{benefit.icon}</div>
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
                <div className="cmc-benefit-glow" style={{ background: `radial-gradient(circle, ${benefit.color}20, transparent)` }} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== FINAL CTA ========== */}
      <section className="cmc-final-cta">
        <div className="cmc-cta-pattern"></div>
        <div className="cmc-cta-inner">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="cmc-cta-icon"
          >
            <GiRocketThruster />
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            Prêt à <span className="cmc-gradient-text">démarrer</span> votre projet ?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            Contactez-nous dès aujourd'hui pour un audit gratuit de votre projet.
          </motion.p>
          <Link to="/reservation-appel">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              className="cmc-cta-button"
            >
              <MdAutoAwesome /> Démarrer mon projet <FaArrowRight />
            </motion.button>
          </Link>
          <div className="cmc-cta-features">
            <div><FaCheckCircle /> Réponse sous 24h</div>
            <div><FaShieldAlt /> Garantie satisfait</div>
            <div><FaUsers /> Support dédié</div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CommentCaMarche;